import {
	OrderQuoteOfferStatus,
	useApplyVetterPricesMutation,
	useGetOfferBatchesForVetterPricesQuery,
	usePreviewApplyVetterPricesQuery,
	type GetOrderQuoteVetterJobsQuery,
} from '@codezo/graphql';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useCurrencyStore } from '../../../stores/currency';
import { Button } from '../../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { SelectPopover } from '../../ui/select-popover';
import { belowCost, parseAmount, type PriceTriple } from '../../pricing/tier-pricing';
import { ApplyPricesProductTable, type ProductPreview } from './ApplyPricesProductTable';
import type { ShipDraft } from './ApplyShippingLines';

type VetterJob = GetOrderQuoteVetterJobsQuery['getOrderQuoteVetterJobs'][number];

/**
 * "Use these prices": one supplier's quote for one product, taken into a draft
 * offer. It opens from that product's table in the supplier's answer.
 *
 * A row per quantity they priced, with the client price set in the same step.
 * The figures are the backend's dry run, the same calculation Apply saves.
 * Apply calls `applyVetterPrices`, which sets the product's supplier and each
 * ticked quantity's cost, production days and, where typed, client price. A
 * new quantity left without one lands with a cost only; the offer can't be
 * sent until it has a client price. All or nothing; a refusal changes nothing.
 * Only draft batches take supplier prices.
 */
export function ApplyVetterPricesModal({
	open,
	onOpenChange,
	quoteId,
	job,
	orderQuoteItemId,
	productName,
	onApplied,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	quoteId: string;
	job: VetterJob | null;
	orderQuoteItemId: string | null;
	productName: string;
	onApplied: () => void;
}) {
	const { selectedCurrency } = useCurrencyStore();
	const { data, loading } = useGetOfferBatchesForVetterPricesQuery({
		variables: { orderQuoteId: quoteId },
		fetchPolicy: 'no-cache',
		skip: !open,
	});
	// Only drafts that carry this product can take its prices; a sent batch has to be revised first.
	const drafts = useMemo(
		() =>
			(data?.getOrderQuoteOfferBatches ?? []).filter(
				(b) =>
					b.status === OrderQuoteOfferStatus.Draft &&
					!b.sentToClientAt &&
					b.offers.some((o) => o.orderQuoteItemId === orderQuoteItemId)
			),
		[data, orderQuoteItemId]
	);
	const [batchId, setBatchId] = useState('');
	const batch = drafts.find((b) => b.id === batchId) ?? drafts[0] ?? null;
	/* A typed delivery price names its target by kind, which the preview line
	   doesn't say: split parts are told apart by the offer's own ids. */
	const partIds = useMemo(
		() =>
			new Set(
				(batch?.offers ?? [])
					.filter((o) => o.orderQuoteItemId === orderQuoteItemId)
					.flatMap((o) => o.pricing)
					.flatMap((p) => p.deliveries)
					.flatMap((d) => d.parts.map((part) => part.id))
			),
		[batch, orderQuoteItemId]
	);

	/* Every quantity the supplier priced for the product. One the offer hasn't
	   priced yet gets its price created, with a cost and no margin. */
	const quantities = useMemo(
		() =>
			(job?.prices ?? [])
				.filter((p) => p.orderQuoteItemId === orderQuoteItemId)
				.sort((a, b) => a.quantity - b.quantity)
				.map((p) => ({ quantityId: p.orderQuoteItemQuantityId, quantity: p.quantity })),
		[job, orderQuoteItemId]
	);

	const [picked, setPicked] = useState<string[]>([]);
	/** Client prices typed per quantity. A quantity with none keeps its rule. */
	const [prices, setPrices] = useState<Record<string, PriceTriple>>({});
	/** Delivery prices typed per quantity, per line. Only lines with a price are sent. */
	const [shipping, setShipping] = useState<Record<string, Record<string, ShipDraft>>>({});
	const [error, setError] = useState<string | null>(null);
	// Each opening, and each batch switch, starts with every quantity ticked.
	useEffect(() => {
		if (!open) return;
		setPicked(quantities.map((q) => q.quantityId));
		setPrices({});
		setShipping({});
		setError(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- reset per opening and batch
	}, [open, batch?.id, quantities.length]);

	/** One input for both the preview and Apply, so the preview IS what Apply saves. */
	const input = useMemo(() => {
		if (!batch || !job || !orderQuoteItemId || picked.length === 0) return null;
		return {
			orderQuoteOfferBatchId: batch.id,
			products: [
				{
					orderQuoteItemId,
					vetterJobId: job.id,
					// Each ticked quantity, with the client and delivery prices only where typed.
					quantities: picked.map((id) => {
						const typed = shippingInput(shipping[id], partIds, selectedCurrency);
						return {
							orderQuoteItemQuantityId: id,
							...clientPrice(prices[id], selectedCurrency),
							...(typed.length > 0 ? { shipping: typed } : {}),
						};
					}),
				},
			],
		};
	}, [batch, job, orderQuoteItemId, picked, prices, shipping, partIds, selectedCurrency]);

	/* The dry run is asked again shortly after the ticks settle. Money is in the
	   display currency, so now and after compare whatever the supplier quoted in. */
	const inputKey = input ? JSON.stringify(input) : '';
	const settledKey = useDebouncedValue(inputKey, 250);
	const { data: previewData, loading: previewLoading } = usePreviewApplyVetterPricesQuery({
		variables: {
			input: settledKey
				? JSON.parse(settledKey)
				: { orderQuoteOfferBatchId: '', products: [] },
			inCurrency: selectedCurrency,
		},
		fetchPolicy: 'no-cache',
		skip: !open || !settledKey,
	});
	// Only trust a preview of exactly what's ticked now.
	const preview =
		settledKey && settledKey === inputKey && !previewLoading
			? previewData?.previewApplyVetterPrices
			: undefined;

	const pending = !!input && !preview;

	/* The figures shown stay the last dry run's while the next one comes back,
	   dimmed, so typing a price doesn't blank the row it's in. */
	const [shown, setShown] = useState<ProductPreview | undefined>();
	useEffect(() => {
		if (preview) setShown(preview.products[0]);
	}, [preview]);
	useEffect(() => {
		if (!open) setShown(undefined);
	}, [open]);
	const rowOf = (id: string) => shown?.quantities.find((r) => r.orderQuoteItemQuantityId === id);

	// A new price without a client price is allowed but can't be sent yet; one under cost is refused.
	const missing = picked.filter(
		(id) => rowOf(id)?.createsPrice && Object.keys(clientPrice(prices[id], '')).length === 0
	);
	const underCost = picked.filter((id) => {
		const row = rowOf(id);
		const p = prices[id];
		return !!row && p?.entryMode === 'sale' && belowCost(String(row.after.cost), p.salePrice);
	});
	const qtyOf = (id: string) =>
		`${(quantities.find((q) => q.quantityId === id)?.quantity ?? 0).toLocaleString('en-US')} pcs`;

	// Days typed on a delivery line with no price: nothing to send them with.
	const daysOnly = picked.flatMap((id) =>
		Object.entries(shipping[id] ?? {})
			.filter(([, d]) => !d.price.trim() && (d.daysMin || d.daysMax))
			.map(([lineId]) => {
				const line = rowOf(id)?.shipping.find((l) => l.id === lineId);
				return `${qtyOf(id)} — ${line?.label ?? 'Shipping'}`;
			})
	);

	const [apply, { loading: applying }] = useApplyVetterPricesMutation({
		refetchQueries: ['GetOrderQuoteOfferBatches'],
	});
	const canApply =
		!!input &&
		!!preview?.canApply &&
		underCost.length === 0 &&
		daysOnly.length === 0 &&
		!applying;

	/** The footer line: what stops Apply, or else a note worth knowing. */
	const blocker =
		error ??
		(picked.length === 0
			? 'Tick at least one quantity.'
			: daysOnly.length > 0
				? `${daysOnly.join(', ')}: add a price, or clear the days`
				: underCost.length > 0
					? `${underCost.map(qtyOf).join(', ')}: below ${job?.vetterEntity?.name ?? 'the supplier'}'s cost`
					: preview && !preview.canApply
						? preview.blockedReason
						: null);
	// Not a problem, just a choice: the client price can wait until the offer is sent.
	const note =
		!blocker && missing.length > 0
			? 'The client price is optional here. You can set it on the offer later, before sending.'
			: null;

	const handleApply = async () => {
		if (!input || !canApply) return;
		setError(null);
		try {
			const { data: res } = await apply({ variables: { input } });
			const result = res?.applyVetterPrices;
			if (!result?.success) {
				setError(result?.message || 'The prices could not be applied.');
				return;
			}
			toast.success(result.message || 'Supplier prices applied');
			onOpenChange(false);
			onApplied();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'The prices could not be applied.');
		}
	};

	const supplierName = job?.vetterEntity?.name ?? 'the supplier';

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>
						Use {supplierName}&apos;s prices for &ldquo;{productName}&rdquo;
					</DialogTitle>
					<DialogDescription>
						Their cost, production time and shipping replace the current ones on the
						offer. Set the client price for each quantity.
					</DialogDescription>
				</DialogHeader>

				<DialogBody>
					{loading && !data ? (
						<div className="flex items-center gap-2 py-4 text-sm text-text-4">
							<Loader2 size={14} className="animate-spin" />
							Loading the offer…
						</div>
					) : !batch ? (
						<div className="py-4 text-sm text-text-3">
							The offer has been sent, so its prices can&apos;t change now. Recall it
							to draft first, then use these prices.
						</div>
					) : (
						<div className="flex flex-col gap-4">
							{drafts.length > 1 && (
								<div className="flex items-center gap-2 text-sm">
									<span className="text-text-3">Offer</span>
									<div className="w-64">
										<SelectPopover
											value={batch.id}
											onChange={setBatchId}
											options={drafts.map((b) => ({
												value: b.id,
												label: b.name,
											}))}
										/>
									</div>
								</div>
							)}
							<div className="flex items-center gap-2 rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-3 py-2.5 text-sm">
								<span className="text-text-3">Supplier</span>
								{shown?.supplierChanges ? (
									<>
										<span className="text-text-4">
											{shown.supplierFrom?.name ?? 'None'}
										</span>
										<ArrowRight size={13} className="text-text-4" />
										<span className="text-text-1">{shown.supplierTo.name}</span>
									</>
								) : (
									<span className="text-text-1">
										{supplierName}
										{shown && (
											<span className="text-text-4">
												{' '}
												· already on the offer
											</span>
										)}
									</span>
								)}
							</div>
							<ApplyPricesProductTable
								quantities={quantities}
								picked={picked}
								onToggle={(quantityId, on) =>
									setPicked((cur) =>
										on
											? [...cur, quantityId]
											: cur.filter((x) => x !== quantityId)
									)
								}
								prices={prices}
								onPriceChange={(id, next) =>
									setPrices((cur) => ({ ...cur, [id]: next }))
								}
								currency={selectedCurrency}
								rowOf={rowOf}
								stale={pending}
								underCost={underCost}
								shipping={shipping}
								onShippingChange={(qid, lineId, next) =>
									setShipping((cur) => ({
										...cur,
										[qid]: { ...cur[qid], [lineId]: next },
									}))
								}
							/>
						</div>
					)}
				</DialogBody>

				<DialogFooter className="sm:items-center">
					<div
						className={
							!blocker
								? 'text-sm text-text-4'
								: error || underCost.length > 0
									? 'text-sm text-status-danger'
									: 'text-sm text-status-warn'
						}
					>
						{batch && (blocker ?? note)}
					</div>
					<div className="flex items-center justify-end gap-2">
						<Button
							variant="ghost"
							onClick={() => onOpenChange(false)}
							disabled={applying}
						>
							Cancel
						</Button>
						<Button onClick={handleApply} disabled={!canApply}>
							{(applying || pending) && (
								<Loader2
									size={14}
									data-icon="inline-start"
									className="animate-spin"
								/>
							)}
							Apply {picked.length} {picked.length === 1 ? 'quantity' : 'quantities'}
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/**
 * The price half of one quantity's input: only the box typed in, since sale
 * price and markup are exclusive. Nothing typed keeps the quantity's rule.
 */
function clientPrice(price: PriceTriple | undefined, currency: string) {
	if (!price) return {};
	if (price.entryMode === 'sale') {
		const sale = parseAmount(price.salePrice);
		return sale == null ? {} : { unitSalePrice: sale, unitSalePriceCurrency: currency };
	}
	const markup = parseAmount(price.markup);
	return markup == null ? {} : { markupPercentage: markup };
}

/**
 * The typed delivery prices of one quantity, as the input wants them: each
 * names its line by kind (an option, a split part, or a new slot by method).
 * A line without a price isn't sent, so it keeps the supplier's or the offer's.
 */
function shippingInput(
	drafts: Record<string, ShipDraft> | undefined,
	partIds: Set<string>,
	currency: string
) {
	const days = (v: string) => (/^\d+$/.test(v) ? Number(v) : undefined);
	return Object.entries(drafts ?? {}).flatMap(([lineId, d]) => {
		const price = parseAmount(d.price);
		if (price == null) return [];
		const target = lineId.startsWith('new:')
			? { shippingMethod: d.shippingMethod }
			: partIds.has(lineId)
				? { orderQuoteOfferDeliveryPartId: lineId }
				: { orderQuoteOfferDeliveryId: lineId };
		return [
			{
				...target,
				price,
				priceCurrency: currency,
				deliveryDaysMin: days(d.daysMin),
				deliveryDaysMax: days(d.daysMax),
			},
		];
	});
}
